import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
PRIVACY POLICY

Last Updated: April 2026

1. INFORMATION WE COLLECT
We collect personal information that you voluntarily provide when purchasing our digital products, including your name, email address, and payment information. We do not store credit card details on our servers — all payments are processed through secure third-party payment processors.

2. HOW WE USE YOUR INFORMATION
We use the information we collect to:
• Process and deliver your digital product purchases
• Send you order confirmations and product access details
• Communicate important updates about your purchase
• Improve our products and customer experience

3. INFORMATION SHARING
We do not sell, trade, or rent your personal information to third parties. We may share your information only with trusted service providers who assist us in operating our website and delivering products, subject to confidentiality agreements.

4. DATA SECURITY
We implement industry-standard security measures to protect your personal information, including SSL encryption and secure payment processing. However, no method of transmission over the Internet is 100% secure.

5. COOKIES
Our website may use cookies to enhance your browsing experience. You can choose to disable cookies through your browser settings, though this may affect certain website functionality.

6. YOUR RIGHTS
You have the right to:
• Access the personal data we hold about you
• Request correction of inaccurate data
• Request deletion of your data
• Opt out of marketing communications at any time

7. CHANGES TO THIS POLICY
We reserve the right to update this Privacy Policy at any time. Changes will be posted on this page with an updated revision date.

8. CONTACT
For any privacy-related questions or requests, please reach out through our official support channels.
`.trim();
const termsContent = `
TERMS OF SERVICE

Last Updated: April 2026

1. ACCEPTANCE OF TERMS
By purchasing and/or using any digital product from 2026 Credit Architecture ("the Company"), you agree to be bound by these Terms of Service. If you do not agree, do not purchase or use our products.

2. DIGITAL PRODUCTS
All products sold are digital information products delivered electronically. Upon successful payment, you will receive immediate access to download your purchased materials. All sales are final except as outlined in our refund policy.

3. REFUND POLICY
We offer a 30-day money-back guarantee. If you follow the steps outlined in the program, mail the letters as instructed, and your credit score does not improve, you may request a full refund within 30 days of purchase. Refund requests must be submitted through our official support channels.

4. INTELLECTUAL PROPERTY
All content, materials, templates, and strategies included in our digital products are protected by copyright and intellectual property laws. You are granted a personal, non-transferable, non-exclusive license to use the materials for your own personal use only. You may NOT:
• Redistribute, resell, or share the materials
• Copy or reproduce the content for commercial purposes
• Claim the materials as your own original work

5. DISCLAIMER
The information provided in our products is for educational purposes only and does not constitute legal, financial, or professional advice. Results may vary based on individual circumstances, effort, and credit profile. We do not guarantee specific credit score improvements or financial outcomes. Past results shared in testimonials are not typical and do not guarantee future performance.

6. LIMITATION OF LIABILITY
To the maximum extent permitted by law, the Company shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our products. Our total liability shall not exceed the amount you paid for the product.

7. GOVERNING LAW
These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles.

8. MODIFICATIONS
We reserve the right to modify these Terms at any time. Continued use of our products after changes constitutes acceptance of the updated Terms.

9. SEVERABILITY
If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-12 px-4 border-t border-white/10" style={{
      backgroundColor: '#080808'
    }} data-id="element-64">
        <div className="max-w-4xl mx-auto text-center" data-id="element-65">
          <p className="text-gray-500 text-sm mb-6 leading-relaxed" data-id="element-66">
            DISCLAIMER: The results stated above are not typical. We are not
            financial advisors, and this is not financial or legal advice. The
            information provided is for educational purposes only. Your results
            will vary depending on your specific credit profile, effort, and
            adherence to the strategies outlined.
          </p>
          <div className="flex justify-center gap-6 text-gray-500 text-sm mb-6" data-id="element-67">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-[#C5A059] transition-colors cursor-pointer underline underline-offset-2" data-id="element-68">
              Privacy Policy
            </button>
            <span className="text-gray-700" data-id="element-69">|</span>
            <button onClick={() => setActiveModal('terms')} className="hover:text-[#C5A059] transition-colors cursor-pointer underline underline-offset-2" data-id="element-70">
              Terms of Service
            </button>
          </div>
          <p className="text-gray-600 text-sm" data-id="element-71">
            &copy; {new Date().getFullYear()} 2026 Credit Architecture. All
            rights reserved.
          </p>
        </div>
      </footer>

      {/* Modal Overlay */}
      <AnimatePresence data-id="element-72">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-73">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-74" />

            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-2xl max-h-[80vh] rounded-2xl border border-white/10 overflow-hidden" style={{
          backgroundColor: '#0c0c14'
        }} data-id="element-75">
              {/* Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/10" style={{
            backgroundColor: '#0c0c14'
          }} data-id="element-76">
                <h2 className="text-xl font-bold" style={{
              color: '#C5A059',
              fontFamily: "'Space Grotesk', sans-serif"
            }} data-id="element-77">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-id="element-78">
                  <X className="w-5 h-5 text-gray-400" data-id="element-79" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)]" data-id="element-80">
                <pre className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap font-sans" data-id="element-81">
                  {activeModal === 'privacy' ? privacyContent : termsContent}
                </pre>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}